import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/blog/ProtectedRoute";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Copy, Check } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface MediaFile {
  name: string;
  id: string;
  created_at: string;
  metadata: any;
}

export default function AdminMedia() {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteFileName, setDeleteFileName] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    setIsLoading(true);
    const { data, error } = await supabase.storage
      .from("blog-images")
      .list(undefined, {
        sortBy: { column: "created_at", order: "desc" },
      });

    if (!error && data) {
      setFiles(data);
    }
    setIsLoading(false);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation for file type and size
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only image files (JPEG, PNG, GIF, WebP, SVG) are allowed');
      e.target.value = ''; // Reset input
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error('File size must be less than 5MB');
      e.target.value = ''; // Reset input
      return;
    }

    setIsUploading(true);
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { error } = await supabase.storage
      .from("blog-images")
      .upload(fileName, file);

    if (error) {
      toast.error("Failed to upload file");
      setIsUploading(false);
      return;
    }

    toast.success("File uploaded successfully");
    setIsUploading(false);
    e.target.value = ''; // Reset input
    fetchFiles();
  };

  const handleDelete = async () => {
    if (!deleteFileName) return;

    const { error } = await supabase.storage
      .from("blog-images")
      .remove([deleteFileName]);

    if (error) {
      toast.error("Failed to delete file");
      return;
    }

    toast.success("File deleted successfully");
    setFiles(files.filter((f) => f.name !== deleteFileName));
    setDeleteFileName(null);
  };

  const getPublicUrl = (fileName: string) => {
    const { data } = supabase.storage
      .from("blog-images")
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const copyToClipboard = (fileName: string) => {
    const url = getPublicUrl(fileName);
    navigator.clipboard.writeText(url);
    setCopiedUrl(url);
    toast.success("URL copied to clipboard");
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  return (
    <ProtectedRoute>
      <AdminLayout>
        <div>
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold">Media Library</h1>
            <div>
              <Input
                id="file-upload"
                type="file"
                accept="image/*"
                onChange={handleUpload}
                disabled={isUploading}
                className="hidden"
              />
              <label htmlFor="file-upload">
                <Button disabled={isUploading} asChild>
                  <span className="cursor-pointer">
                    {isUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Image
                      </>
                    )}
                  </span>
                </Button>
              </label>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : files.length === 0 ? (
            <Card>
              <CardContent className="py-20 text-center">
                <p className="text-muted-foreground mb-4">No images yet</p>
                <label htmlFor="file-upload">
                  <Button asChild>
                    <span className="cursor-pointer">Upload Your First Image</span>
                  </Button>
                </label>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {files.map((file) => {
                const publicUrl = getPublicUrl(file.name);
                const isCopied = copiedUrl === publicUrl;
                
                return (
                  <Card key={file.id} className="overflow-hidden group">
                    <div className="aspect-square relative">
                      <img
                        src={publicUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          size="icon"
                          variant="secondary"
                          onClick={() => copyToClipboard(file.name)}
                        >
                          {isCopied ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="destructive"
                          onClick={() => setDeleteFileName(file.name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <CardContent className="p-2">
                      <p className="text-xs truncate text-muted-foreground">
                        {file.name}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        <AlertDialog
          open={deleteFileName !== null}
          onOpenChange={() => setDeleteFileName(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the image.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AdminLayout>
    </ProtectedRoute>
  );
}
